// src/components/Center Admin/forms/ADMinorSymptomsExaminationForm.tsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import type { ADFeature, YesNo } from "../../../types/nurseFormTypes";
import "../../../center admin css/ADMinorSymptomsExaminationForm.css";
import "../../../center admin css/centerAdmin.css";
import Facial_Pallor_Erythema from "../../../assets/images/Facial Pallor  Erythema.jpg";
import White_Patches from "../../../assets/images/White Patches.jpg";
import Darkening_around_the_Eyelids from "../../../assets/images/Darkening around the Eyelids.jpg";
import Infra_Orbital_Folds from "../../../assets/images/Infra-Orbital Folds.jpg";
import Dry_Lips_Cheilitis from "../../../assets/images/Dry Lips (Cheilitis).jpg";
import Anterior_Neck_Folds from "../../../assets/images/Anterior Neck-Folds.jpg";
import Dry_Skin_Ichthyosis from "../../../assets/images/Dry Skin (Ichthyosis).jpg";
import Increased_Lines_Over_Palms from "../../../assets/images/Increased Lines Over Palms.jpg";
import Hand_And_Foot_Eczema from "../../../assets/images/Hand And Foot Eczema.jpg";
import Nipple_Eczema from "../../../assets/images/Nipple Eczema.jpg";
import api from "../../../api/axiosConfig";

// make sure useS3Uploader exists and returns uploadImage(file, modulePath) -> { url }
import { useS3Uploader } from "../../../hooks/useS3Uploader"; // <-- correct

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

const INITIAL_FEATURES: ADFeature[] = [
  {
    key: "facial_pallor",
    title: "Facial Pallor / Erythema",
    observed: "",
    referenceImage: Facial_Pallor_Erythema,

    uploadedUrls: [],
  },
  {
    key: "pityriasis_alba",
    title: "White Patches",
    observed: "",
    referenceImage: White_Patches,
    uploadedUrls: [],
  },
  {
    key: "darkening_around_the_eyelids",
    title: "Darkening around the Eyelids",
    observed: "",
    referenceImage: Darkening_around_the_Eyelids,
    uploadedUrls: [],
  },
  {
    key: "infra_orbital_folds",
    title: "Infra-Orbital Folds",
    observed: "",
    referenceImage: Infra_Orbital_Folds,
    uploadedUrls: [],
  },
  {
    key: "dry_lips",
    title: "Dry Lips (Cheilitis)",
    observed: "",
    referenceImage: Dry_Lips_Cheilitis,
    uploadedUrls: [],
  },
  {
    key: "anterior_neck_folds",
    title: "Anterior Neck-Folds",
    observed: "",
    referenceImage: Anterior_Neck_Folds,
    uploadedUrls: [],
  },
  {
    key: "ichthyosis",
    title: "Dry Skin (Ichthyosis)",
    observed: "",
    referenceImage: Dry_Skin_Ichthyosis,
    uploadedUrls: [],
  },
  {
    key: "increased_palmar_lines",
    title: "Increased Lines Over Palms",
    observed: "",
    referenceImage: Increased_Lines_Over_Palms,
    uploadedUrls: [],
  },
  {
    key: "hand_and_foot_eczema",
    title: "Hand And Foot Eczema",
    observed: "",
    referenceImage: Hand_And_Foot_Eczema,
    uploadedUrls: [],
  },
  {
    key: "nipple_eczema",
    title: "Nipple Eczema",
    observed: "",
    referenceImage: Nipple_Eczema,
    uploadedUrls: [],
  },
];

export const AD_S3_MAP: Record<string, string> = {
  facial_pallor: "ad-minor-symptoms/facial-pallor",
  // white_patches: "ad-minor-symptoms/white-patches",
  pityriasis_alba: "ad-minor-symptoms/white-patches",
  // darkening_around_eyelids: "ad-minor-symptoms/darkening-around-the-eyelids",
  darkening_around_the_eyelids: "ad-minor-symptoms/darkening-around-eyelids",
  infra_orbital_folds: "ad-minor-symptoms/infra-orbital-folds",
  dry_lips: "ad-minor-symptoms/dry-lips",
  anterior_neck_folds: "ad-minor-symptoms/anterior-neckfold",
  ichthyosis: "ad-minor-symptoms/dry-skin",
  increased_palmar_lines: "ad-minor-symptoms/increased-lines-over-palms",
  hand_and_foot_eczema: "ad-minor-symptoms/hand-and-foot-eczema",
  nipple_eczema: "ad-minor-symptoms/nipple-eczema",
};

export const ADMinorSymptomsExaminationForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  const [adFeatures, setAdFeatures] = useState<ADFeature[]>(INITIAL_FEATURES);
  const [errors, setErrors] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resetForm = () => {
    setAdFeatures(INITIAL_FEATURES);

    setErrors({});

    setSuccess(false);

    setError(null);
  };

  // ---------------------------------------------------------
  // LOAD EXISTING ATOPIC DERMATITIS DATA
  // ---------------------------------------------------------

  const loadExistingADData = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/atopic-dermatitis/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved?.features) {
        setAdFeatures(
          INITIAL_FEATURES.map((feature) => ({
            ...feature,
            observed: "",
            uploadedUrls: [],
          })),
        );
        return;
      }

      const restoredFeatures: ADFeature[] = await Promise.all(
        INITIAL_FEATURES.map(async (feature) => {
          const savedFeature = saved.features.find(
            (item: any) => item.code === feature.key,
          );

          if (!savedFeature) {
            return {
              ...feature,
              observed: "",
              uploadedUrls: [],
            };
          }

          const restoredImages: {
            key: string;
            previewUrl?: string;
            originalFilename?: string;
          }[] = [];

          const savedImages = Array.isArray(savedFeature.imageUrls)
            ? savedFeature.imageUrls
            : [];

          for (const imageItem of savedImages) {
            try {
              // API may return either an S3 key string
              // or an object containing the key.
              const imageKey =
                typeof imageItem === "string" ? imageItem : imageItem?.key;

              if (!imageKey || typeof imageKey !== "string") {
                continue;
              }

              const parts = imageKey.split("/");

              if (parts.length < 3) {
                console.warn("Invalid AD S3 key:", imageKey);
                continue;
              }

              // Example:
              // ad-minor/atopic/P-2026-000111-2/facial-pallor/file.jpg
              const module = `${parts[0]}/${parts[1]}`;
              const objectKey = parts.slice(2).join("/");

              const viewRes = await api.S3config.get("/view-url", {
                params: {
                  module,
                  key: objectKey,
                },
              });

              const signedUrl = viewRes.data?.url || (viewRes as any).url;

              if (!signedUrl) {
                console.warn("No signed URL returned for AD image:", imageKey);
                continue;
              }

              restoredImages.push({
                key: imageKey,
                previewUrl: signedUrl,
                originalFilename:
                  typeof imageItem === "object" && imageItem?.originalFilename
                    ? imageItem.originalFilename
                    : (imageKey.split("/").pop() ?? "Uploaded image"),
              });
            } catch (imageErr) {
              console.error("Failed to load AD image:", imageItem, imageErr);
            }
          }

          return {
            ...feature,
            observed: savedFeature.observed ? "yes" : "no",
            uploadedUrls: restoredImages,
          };
        }),
      );

      // IMPORTANT: put the restored data into React state.
      setAdFeatures(restoredFeatures);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAdFeatures(
          INITIAL_FEATURES.map((feature) => ({
            ...feature,
            observed: "",
            uploadedUrls: [],
          })),
        );
        return;
      }

      console.error("Failed to load existing Atopic Dermatitis data:", err);
    }
  };

  useEffect(() => {
    loadExistingADData();
  }, [emrId]);

  // Reuse same uploader hook used in OralImaging (returns { uploadImage })
  const { uploadImage } = useS3Uploader(emrId);

  const setObserved = (idx: number, value: YesNo) => {
    setAdFeatures((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, observed: value } : f)),
    );
    setErrors((prev) => ({ ...prev, [idx]: false }));
  };

  // Upload multiple files for single feature index
  const uploadImages = async (idx: number, files: FileList | null) => {
    if (!files || files.length === 0) return;

    // convert to Array so we can await sequentially if required
    const list = Array.from(files);
    // const urls: string[] = [];
    const uploads: {
      key: string;
      previewUrl?: string;
      originalFilename?: string;
    }[] = [];

    for (const file of list) {
      // verify mapping exists BEFORE calling backend
      const mapKey = AD_S3_MAP[adFeatures[idx].key];
      if (!mapKey) {
        console.error("No AD_S3_MAP entry for key:", adFeatures[idx].key);
        continue;
      }

      try {
        // call your presign + upload helper (should return the final GET url)
        const result = await uploadImage(
          file,
          AD_S3_MAP[adFeatures[idx].key], // always "ad-minor-symptoms"
          undefined, // no slot in frontend, backend auto-extracts
        );

        if (result) {
          uploads.push({
            key: result.key,
            previewUrl: result.previewUrl,
            originalFilename: result.originalFilename,
          });
        }
      } catch (err) {
        console.error("Upload failed for", file.name, err);
      }
    }

    if (uploads.length > 0) {
      setAdFeatures((prev) =>
        prev.map((f, i) =>
          i === idx
            ? {
                ...f,
                uploadedUrls: [...(f.uploadedUrls || []), ...uploads],
              }
            : f,
        ),
      );
    }
  };

  const removeUploadedImage = (featureIndex: number, imageIndex: number) => {
    setAdFeatures((prev) =>
      prev.map((feature, idx) =>
        idx === featureIndex
          ? {
              ...feature,
              uploadedUrls: (feature.uploadedUrls || []).filter(
                (_, i) => i !== imageIndex,
              ),
            }
          : feature,
      ),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ only after successful validation
    // onComplete && onComplete();

    // basic validation: all features must have observed selected
    const newErrors: Record<number, boolean> = {};
    adFeatures.forEach((f, idx) => {
      if (!f.observed) newErrors[idx] = true;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setSuccess(false);
    setError(null);

    // Build payload: each feature -> imageUrls array (could be empty)
    const payload = {
      emrId,
      features: adFeatures.map((f) => ({
        code: f.key,
        label: f.title,
        observed: f.observed === "yes",
        imageUrls: (f.uploadedUrls ?? []).map((image) => image.key),
      })),
    };

    try {
      const res = await api.dataCollectionApi.post(
        "/api/forms/atopic-dermatitis",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (res.data?.success) {
        alert(
          "Atopic Dermatitis Minor Symptoms Examination submitted successfully!",
        );

        setSuccess(true);

        resetForm();

        onComplete && onComplete();
      } else {
        throw new Error(res.data?.message || "Save failed — invalid response");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="form-section" onSubmit={handleSubmit}>
      <div className="vs-section-bar">Atopic Dermatitis Features</div>

      <div className="form-body ad-body">
        <div className="ad-grid">
          {/* <div style={{ color: "red", fontSize: "20px" }}>
            TOTAL FEATURES: {adFeatures.length}
          </div> */}
          {adFeatures.map((f, idx) => (
            <div key={f.key} className="ad-card">
              <div className="ad-card-head">
                <div className="ad-title">{f.title}</div>
              </div>

              <div className="ad-card-body">
                {/* multiple preview images */}
                <div className="ad-preview">
                  {f.referenceImage ? (
                    <img src={f.referenceImage} alt={f.title} />
                  ) : (
                    <div className="ad-preview-placeholder" />
                  )}
                </div>

                <div className="ad-upload">
                  <div className="ad-upload-box">
                    <div className="ad-upload-icon">⬆</div>
                    <div className="ad-upload-text">Upload image</div>

                    {/* IMPORTANT: allow multiple files and call uploadImages */}
                    <label className="ad-choose-btn">
                      Choose Files
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          uploadImages(idx, e.target.files);
                          // optionally also show temp preview for immediate UX:
                        }}
                      />
                    </label>

                    <div className="ad-hint">
                      Max 5MB per file, JPG/PNG only. You can upload multiple
                      images.
                    </div>

                    {(f.uploadedUrls?.length ?? 0) > 0 && (
                      <div className="ad-uploaded-list">
                        {(f.uploadedUrls ?? []).map((url, index) => (
                          <div key={index} className="ad-uploaded-item">
                            <img
                              src={url.previewUrl}
                              alt={`Upload ${index + 1}`}
                              className="ad-uploaded-thumbnail"
                            />

                            <div className="ad-uploaded-details">
                              <div className="ad-uploaded-name">
                                {url.originalFilename}
                              </div>

                              <button
                                type="button"
                                className="ad-remove-btn"
                                onClick={() => removeUploadedImage(idx, index)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className={`ad-observed ${
                  errors[idx] ? "control-group-error" : ""
                }`}
              >
                <span className="muted">Observed?</span>

                <label className="ack-radio">
                  <input
                    type="radio"
                    name={`obs_${f.key}`}
                    checked={f.observed === "no"}
                    onChange={() => setObserved(idx, "no")}
                  />
                  <span className="radio-custom" />
                  <span className="radio-label">No</span>
                </label>

                <label className="ack-radio">
                  <input
                    type="radio"
                    name={`obs_${f.key}`}
                    checked={f.observed === "yes"}
                    onChange={() => setObserved(idx, "yes")}
                  />
                  <span className="radio-custom" />
                  <span className="radio-label">Yes</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Assessment"}
        </button>
      </div>

      {success && (
        <p style={{ color: "green" }}>Form submitted successfully!</p>
      )}
      {error && <p style={{ color: "red" }}>{error}</p>}
    </form>
  );
};

export default ADMinorSymptomsExaminationForm;
